import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import ImageUploader from './components/ImageUploader';
import ResultsPanel from './components/ResultsPanel';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9456';

function App() {
  const [originalImage, setOriginalImage] = useState(null);
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [detections, setDetections] = useState([]);
  const [imageInfo, setImageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.25);
  const [sidePanelOpen, setSidePanelOpen] = useState(true);

  // Versioning
  const [selectedVersion, setSelectedVersion] = useState('v3.0');
  const [compareMode, setCompareMode] = useState('single'); // 'single', 'all'
  const availableVersions = ['v1.0', 'v2.0', 'v3.0'];

  // Risultati confronto (per modalità compare)
  const [comparisonResults, setComparisonResults] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);

  // Stato per pannello espanso e selezionato (compare mode)
  const [expandedPanel, setExpandedPanel] = useState(null); // null o 'v1.0', 'v2.0', 'v3.0'
  const [selectedPanel, setSelectedPanel] = useState('v3.0'); // versione selezionata per side panel

  // Ref per input file nascosto (per sostituire immagine)
  const fileInputRef = useRef(null);

  // Funzione per chiamare API con una versione specifica
  const predictWithVersion = useCallback(async (file, version, threshold) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/predict?confidence_threshold=${threshold}&include_image=true&version=${version}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }, []);

  // Funzione principale di analisi
  const runAnalysis = useCallback(async (file, version, isCompareMode, threshold) => {
    if (!file) return;

    setError(null);
    setLoading(true);

    try {
      if (isCompareMode === 'all') {
        // Modalità confronto: chiama tutte le versioni in parallelo
        const [resultV1, resultV2, resultV3] = await Promise.all([
          predictWithVersion(file, 'v1.0', threshold),
          predictWithVersion(file, 'v2.0', threshold),
          predictWithVersion(file, 'v3.0', threshold)
        ]);

        const results = {
          'v1.0': resultV1,
          'v2.0': resultV2,
          'v3.0': resultV3
        };
        setComparisonResults(results);

        // Imposta la versione selezionata di default a v3.0
        setSelectedPanel('v3.0');
        setExpandedPanel(null);

        // Mostra risultati della versione selezionata nel side panel
        const mainResult = results['v3.0'];
        if (mainResult?.success) {
          setImageInfo(mainResult.image_info);
          setDetections(mainResult.detections);
          if (mainResult.annotated_image_base64) {
            setAnnotatedImage(`data:image/png;base64,${mainResult.annotated_image_base64}`);
          }
        }
      } else {
        // Modalità normale (single): chiama solo la versione selezionata
        setComparisonResults(null);
        const data = await predictWithVersion(file, version, threshold);

        if (data.success) {
          setImageInfo(data.image_info);
          setDetections(data.detections);
          if (data.annotated_image_base64) {
            setAnnotatedImage(`data:image/png;base64,${data.annotated_image_base64}`);
          } else {
            setAnnotatedImage(null);
          }
        } else {
          setError(data.error || 'Errore durante la predizione');
        }
      }
    } catch (err) {
      console.error('Errore:', err);
      setError(err.response?.data?.detail || err.message || 'Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  }, [predictWithVersion]);

  // Handler per selezione nuova immagine
  const handleImageSelect = useCallback(async (file) => {
    setAnnotatedImage(null);
    setDetections([]);
    setImageInfo(null);
    setComparisonResults(null);
    setCurrentFile(file);

    const reader = new FileReader();
    reader.onload = (e) => setOriginalImage(e.target.result);
    reader.readAsDataURL(file);

    // Esegui analisi
    await runAnalysis(file, selectedVersion, compareMode, confidenceThreshold);
  }, [selectedVersion, compareMode, confidenceThreshold, runAnalysis]);

  // Ri-esegui analisi quando cambiano versione, modalità o threshold (se c'è già un file)
  useEffect(() => {
    if (currentFile && originalImage) {
      runAnalysis(currentFile, selectedVersion, compareMode, confidenceThreshold);
    }
    // eslint-disable-next-line
  }, [selectedVersion, compareMode, confidenceThreshold]);

  const handleReset = () => {
    setOriginalImage(null);
    setAnnotatedImage(null);
    setDetections([]);
    setImageInfo(null);
    setError(null);
    setComparisonResults(null);
    setCurrentFile(null);
  };

  // Click sull'immagine per sostituirla
  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Gestione file selezionato da input nascosto
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageSelect(files[0]);
    }
    // Reset input per permettere di selezionare lo stesso file
    e.target.value = '';
  };

  // Handler per click su pannello in compare mode
  const handlePanelClick = (version, e) => {
    e.stopPropagation();

    if (expandedPanel === version) {
      // Se già espanso, chiudi
      setExpandedPanel(null);
    } else if (expandedPanel) {
      // Se un altro è espanso, cambia espansione
      setExpandedPanel(version);
    } else {
      // Altrimenti seleziona per side panel (singolo click)
      setSelectedPanel(version);
      // Aggiorna info nel side panel
      if (comparisonResults?.[version]?.success) {
        setImageInfo(comparisonResults[version].image_info);
        setDetections(comparisonResults[version].detections);
      }
    }
  };

  // Handler per doppio click - espande/comprime
  const handlePanelDoubleClick = (version, e) => {
    e.stopPropagation();
    if (expandedPanel === version) {
      setExpandedPanel(null);
    } else {
      setExpandedPanel(version);
      setSelectedPanel(version);
      // Aggiorna info nel side panel
      if (comparisonResults?.[version]?.success) {
        setImageInfo(comparisonResults[version].image_info);
        setDetections(comparisonResults[version].detections);
      }
    }
  };

  return (
    <div className="app">
      {/* Header fisso */}
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">P</span>
            <span className="logo-text">Print Designer Creator</span>
            <span className="version">PDC 1.0</span>
          </div>
        </div>

        <div className="header-center">
          <label className="threshold-control">
            <span>Confidence: {(confidenceThreshold * 100).toFixed(0)}%</span>
            <input
              type="range"
              min="0"
              max="100"
              value={confidenceThreshold * 100}
              onChange={(e) => setConfidenceThreshold(e.target.value / 100)}
            />
          </label>

          <div className="version-controls">
            <select
              className="version-select"
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              disabled={compareMode === 'all'}
            >
              <option value="v1.0">v1.0 (Local)</option>
              <option value="v2.0">v2.0 (Azure)</option>
              <option value="v3.0">v3.0 (Azure)</option>
            </select>

            <button
              className={`compare-btn ${compareMode === 'all' ? 'active' : ''}`}
              onClick={() => setCompareMode(compareMode === 'all' ? 'single' : 'all')}
              title={compareMode === 'all' ? 'Mostra singola versione' : 'Confronta tutte le versioni'}
            >
              {compareMode === 'all' ? 'Single' : 'Compare All'}
            </button>
          </div>
        </div>

        <div className="header-right">
          {detections.length > 0 && (
            <button
              className={`panel-toggle ${sidePanelOpen ? 'active' : ''}`}
              onClick={() => setSidePanelOpen(!sidePanelOpen)}
              title="Toggle details panel"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
              Details
            </button>
          )}
          {originalImage && (
            <button className="reset-btn" onClick={handleReset}>
              Reset
            </button>
          )}
        </div>
      </header>

      {/* Contenuto principale */}
      <main className={`main ${sidePanelOpen && detections.length > 0 ? 'with-panel' : ''} ${compareMode === 'all' ? 'compare-mode' : ''} ${expandedPanel ? 'has-expanded' : ''}`}>
        <div className="content">
          {/* Immagine originale */}
          <div className={`panel input-panel ${expandedPanel ? 'hidden' : ''}`}>
            <div className="panel-header">
              <h2>Input Image</h2>
              {originalImage && (
                <span className="change-hint">Click to change</span>
              )}
            </div>
            <div
              className={`image-container ${originalImage ? 'clickable' : ''}`}
              onClick={originalImage ? handleImageClick : undefined}
            >
              {!originalImage ? (
                <ImageUploader onImageSelect={handleImageSelect} />
              ) : (
                <img src={originalImage} alt="Original" className="preview-image" />
              )}
            </div>
            {/* Input file nascosto per sostituire immagine */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Modalità confronto: mostra tutte le versioni */}
          {compareMode === 'all' ? (
            <>
              {availableVersions.map((ver) => {
                const isExpanded = expandedPanel === ver;
                const isSelected = selectedPanel === ver;
                const isHidden = expandedPanel && expandedPanel !== ver;

                return (
                  <div
                    className={`panel result-panel ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''} ${isHidden ? 'hidden' : ''}`}
                    key={ver}
                    onClick={(e) => handlePanelClick(ver, e)}
                    onDoubleClick={(e) => handlePanelDoubleClick(ver, e)}
                  >
                    <div className="panel-header">
                      <h2>{ver} Results</h2>
                      <span className={`version-badge ${ver.replace('.', '')}`}>
                        {ver === 'v1.0' ? 'Local' : 'Azure ML'}
                      </span>
                      {comparisonResults?.[ver]?.detections?.length > 0 && (
                        <span className="detection-count">
                          {comparisonResults[ver].detections.length} area{comparisonResults[ver].detections.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {isExpanded && (
                        <button
                          className="collapse-btn"
                          onClick={(e) => { e.stopPropagation(); setExpandedPanel(null); }}
                          title="Chiudi espansione"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="image-container">
                      {loading ? (
                        <div className="loading">
                          <div className="spinner"></div>
                          <p>Analyzing with {ver}...</p>
                        </div>
                      ) : comparisonResults?.[ver]?.annotated_image_base64 ? (
                        <img
                          src={`data:image/png;base64,${comparisonResults[ver].annotated_image_base64}`}
                          alt={`${ver} Result`}
                          className="preview-image"
                        />
                      ) : originalImage ? (
                        <div className="placeholder">
                          <p>No areas detected</p>
                        </div>
                      ) : (
                        <div className="placeholder">
                          <p>Upload an image to compare</p>
                        </div>
                      )}
                    </div>
                    {!isExpanded && (
                      <div className="panel-hint">
                        {isSelected ? 'Double-click to expand' : 'Click to select'}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            /* Modalità normale: solo versione selezionata */
            <div className="panel">
              <div className="panel-header">
                <h2>Detected Areas</h2>
                <span className="version-badge">{selectedVersion}</span>
                {detections.length > 0 && (
                  <span className="detection-count">{detections.length} area{detections.length > 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="image-container">
                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                    <p>Analyzing with {selectedVersion}...</p>
                  </div>
                ) : error ? (
                  <div className="error">
                    <p>{error}</p>
                  </div>
                ) : annotatedImage ? (
                  <img src={annotatedImage} alt="Annotated" className="preview-image" />
                ) : originalImage ? (
                  <div className="placeholder">
                    <p>No areas detected</p>
                  </div>
                ) : (
                  <div className="placeholder">
                    <p>Upload an image to see results</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pannello laterale */}
        {detections.length > 0 && (
          <aside className={`side-panel ${sidePanelOpen ? 'open' : 'closed'}`}>
            {compareMode === 'all' && (
              <div className="side-panel-header">
                <span className={`version-badge ${selectedPanel.replace('.', '')}`}>
                  {selectedPanel}
                </span>
                <span className="selected-label">Selected</span>
              </div>
            )}
            <ResultsPanel detections={detections} imageInfo={imageInfo} />
          </aside>
        )}
      </main>
    </div>
  );
}

export default App;
